/**
 * Mutations du module YouTube Studio (admin).
 *
 * CRUD vidéos / assets = écritures Supabase directes (RLS admin, migration 0107).
 * L'enqueue de rendu et l'OAuth passent par des edge functions (logique serveur,
 * secrets). Les hooks invalident les queries concernées.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  VideoSpec,
  EnqueueRenderInput,
  YoutubeAssetKind,
  MusicCredit,
} from '@jeraisonne/db/schemas/youtube';
import { playlistItemRows } from '@jeraisonne/db/youtube';
import { supabase } from '@/lib/supabase';
import { ytDb, type YoutubeAssetRow, type YoutubeVideoRow } from './youtube-queries';

/** Invoque une edge function et remonte un message d'erreur lisible. */
async function invokeFn<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, {
    body: body as Record<string, unknown>,
  });
  if (error) {
    let message = error.message;
    try {
      const ctxBody = await (
        error as { context?: { json?: () => Promise<unknown> } }
      ).context?.json?.();
      const errObj = ctxBody as { error?: string; message?: string } | undefined;
      message = errObj?.message ?? errObj?.error ?? message;
    } catch {
      /* garde le message générique */
    }
    throw new Error(message);
  }
  if (data == null) throw new Error('Réponse vide de la fonction.');
  return data;
}

// ── Vidéos (CRUD direct, RLS admin) ──────────────────────────────────────────

export function useUpsertYoutubeVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; userId: string; spec: VideoSpec }) => {
      // `items` est persistée dans youtube_video_items (table dédiée), pas sur la
      // ligne youtube_videos → on la retire du payload. exercise_id/attempt_id de
      // la ligne = item primaire (items[0], posé par l'éditeur).
      const { items, ...spec } = input.spec;
      const row = {
        ...spec,
        created_by: input.userId,
      };
      const query = input.id
        ? ytDb.from('youtube_videos').update(row).eq('id', input.id).select('*').maybeSingle()
        : ytDb.from('youtube_videos').insert(row).select('*').maybeSingle();
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      const video = data as YoutubeVideoRow;

      // Remplace la playlist : purge puis réinsère dans l'ordre (positions 0..n).
      // Écritures directes RLS admin, cohérent avec le reste du module.
      const { error: delErr } = await ytDb
        .from('youtube_video_items')
        .delete()
        .eq('video_id', video.id);
      if (delErr) throw new Error(delErr.message);
      const { error: insErr } = await ytDb
        .from('youtube_video_items')
        .insert(playlistItemRows(video.id, items));
      if (insErr) throw new Error(insErr.message);

      return video;
    },
    onSuccess: (video) => {
      qc.invalidateQueries({ queryKey: ['youtube-videos'] });
      qc.invalidateQueries({ queryKey: ['youtube-video', video.id] });
    },
  });
}

export function useDeleteYoutubeVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (videoId: string) => {
      const { error } = await ytDb.from('youtube_videos').delete().eq('id', videoId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['youtube-videos'] }),
  });
}

// ── Assets (upload Storage + insert métadonnées) ─────────────────────────────

export interface UploadAssetInput {
  userId: string;
  file: File;
  kind: YoutubeAssetKind;
  label: string;
  credit?: MusicCredit;
}

export function useUploadYoutubeAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadAssetInput): Promise<YoutubeAssetRow> => {
      const safeName = input.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${input.userId}/${input.kind}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from('youtube-assets')
        .upload(path, input.file, { contentType: input.file.type, upsert: false });
      if (upErr) throw new Error(upErr.message);

      const row = {
        created_by: input.userId,
        kind: input.kind,
        label: input.label || safeName,
        bucket_path: path,
        mime: input.file.type,
        bytes: input.file.size,
        music_artist: input.credit?.music_artist ?? null,
        music_license: input.credit?.music_license ?? null,
        music_source_url: input.credit?.music_source_url ?? null,
      };
      const { data, error } = await ytDb
        .from('youtube_assets')
        .insert(row)
        .select('*')
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as YoutubeAssetRow;
    },
    onSuccess: (asset) => {
      qc.invalidateQueries({ queryKey: ['youtube-assets', asset.kind] });
      qc.invalidateQueries({ queryKey: ['youtube-assets', 'all'] });
    },
  });
}

/** URL signée d'un asset privé (prévisualisation). */
export async function signedAssetUrl(bucketPath: string, expiresIn = 3600): Promise<string | null> {
  const { data } = await supabase.storage
    .from('youtube-assets')
    .createSignedUrl(bucketPath, expiresIn);
  return data?.signedUrl ?? null;
}

// ── Rendu & publication (edge function) ──────────────────────────────────────

export function useEnqueueRender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EnqueueRenderInput) =>
      invokeFn<{ jobId: string }>('youtube-enqueue', input),
    onSuccess: (_res, input) => {
      qc.invalidateQueries({ queryKey: ['youtube-videos'] });
      qc.invalidateQueries({ queryKey: ['youtube-video', input.video_id] });
    },
  });
}

export function useCancelYoutubeJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { videoId: string; jobId: string }) => {
      const { error } = await ytDb
        .from('youtube_jobs')
        .update({ cancellation_requested: true })
        .eq('id', input.jobId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_r, input) => qc.invalidateQueries({ queryKey: ['youtube-video', input.videoId] }),
  });
}

/** Démarre le flux OAuth YouTube — renvoie l'URL de consentement Google. */
export function useStartYoutubeOAuth() {
  return useMutation({
    mutationFn: () => invokeFn<{ url: string }>('youtube-oauth-start', {}),
  });
}
